/**
 * MultiplayerClient — WebSocket multiplayer for the survival game.
 *
 * Connects to ws(s)://<host>/ws/<roomId>
 * Sends player state at ~20 Hz (every SYNC_INTERVAL ms).
 * Renders remote players on the game canvas via drawRemotePlayers(ctx).
 */

const SYNC_INTERVAL = 50; // ms — ~20 Hz
const LERP_FACTOR = 0.1;  // position interpolation per frame

export class MultiplayerClient {
    constructor() {
        this._ws = null;
        this._connected = false;
        this._syncInterval = null;

        // Local player reference (set via setPlayer)
        this._player = null;
        this._roomId = null;
        this._playerName = 'Player';
        this._skin = {};

        // Remote players: playerId -> state object
        this._remotePlayers = new Map();

        // Event callbacks
        this._listeners = {
            connected: [],
            disconnected: [],
            player_joined: [],
            player_left: []
        };
    }

    // -------------------------------------------------------
    // Public API
    // -------------------------------------------------------

    /** Open a WebSocket connection to the given room. */
    connect(roomId, playerName, skin) {
        if (this._ws) {
            this.disconnect();
        }

        this._roomId = roomId;
        this._playerName = playerName || 'Player';
        this._skin = skin || {};

        const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const wsUrl = `${protocol}://${window.location.host}/ws/${roomId}`;

        try {
            this._ws = new WebSocket(wsUrl);
        } catch (err) {
            console.warn('[Multiplayer] Could not create WebSocket:', err);
            return;
        }

        this._ws.addEventListener('open', () => {
            this._connected = true;
            this._emit('connected');
            this._startSync();
        });

        this._ws.addEventListener('message', (event) => {
            this._handleMessage(event.data);
        });

        this._ws.addEventListener('close', () => {
            this._connected = false;
            this._stopSync();
            this._emit('disconnected');
        });

        this._ws.addEventListener('error', (err) => {
            console.warn('[Multiplayer] WebSocket error:', err);
        });
    }

    /** Close the WebSocket and stop syncing. */
    disconnect() {
        this._stopSync();
        if (this._ws) {
            this._ws.close();
            this._ws = null;
        }
        this._connected = false;
        this._remotePlayers.clear();
    }

    /** Store a reference to the local player so the sync interval can read its state. */
    setPlayer(player) {
        this._player = player;
    }

    /** Manually send the current player state (also called by the sync interval). */
    sendState(player) {
        if (!this._connected || !this._ws) return;
        const p = player || this._player;
        if (!p) return;

        this._wsSend({
            type: 'state',
            x: p.x,
            y: p.y,
            hp: p.hp,
            coins: p.coins,
            kills: p.kills || 0,
            name: p.name || this._playerName,
            skin: p.skin || this._skin
        });
    }

    /** Draw all remote players onto the canvas context (world-space coords). */
    drawRemotePlayers(ctx) {
        if (this._remotePlayers.size === 0) return;

        ctx.save();

        for (const [, rp] of this._remotePlayers) {
            // Smooth interpolation toward target position
            rp.x = rp.x + (rp.targetX - rp.x) * LERP_FACTOR;
            rp.y = rp.y + (rp.targetY - rp.y) * LERP_FACTOR;

            this._drawRemotePlayer(ctx, rp);
        }

        ctx.restore();
    }

    /** Returns an array of remote player state objects. */
    getRemotePlayers() {
        return Array.from(this._remotePlayers.values());
    }

    /** Total players in the room, including the local player. */
    getRoomPlayerCount() {
        return this._remotePlayers.size + (this._connected ? 1 : 0);
    }

    /** Whether the WebSocket is currently open and connected. */
    isConnected() {
        return this._connected;
    }

    /** Register an event listener. Events: 'connected', 'disconnected', 'player_joined', 'player_left'. */
    on(event, fn) {
        if (this._listeners[event]) {
            this._listeners[event].push(fn);
        }
    }

    // -------------------------------------------------------
    // Private helpers
    // -------------------------------------------------------

    _startSync() {
        this._stopSync();
        this._syncInterval = setInterval(() => {
            this.sendState(this._player);
        }, SYNC_INTERVAL);
    }

    _stopSync() {
        if (this._syncInterval !== null) {
            clearInterval(this._syncInterval);
            this._syncInterval = null;
        }
    }

    _wsSend(obj) {
        if (this._ws && this._ws.readyState === WebSocket.OPEN) {
            try {
                this._ws.send(JSON.stringify(obj));
            } catch (e) {
                console.warn('[Multiplayer] send error:', e);
            }
        }
    }

    _emit(event, ...args) {
        (this._listeners[event] || []).forEach(fn => fn(...args));
    }

    _handleMessage(rawText) {
        let msg;
        try {
            msg = JSON.parse(rawText);
        } catch {
            return;
        }

        switch (msg.type) {
            case 'joined': {
                // Server sends the list of currently connected players
                (msg.players || []).forEach(p => {
                    this._upsertRemotePlayer(p.playerId, p);
                });
                break;
            }

            case 'player_joined': {
                this._upsertRemotePlayer(msg.playerId, {
                    name: msg.name || 'Player',
                    skin: msg.skin || {},
                    x: 0,
                    y: 0,
                    hp: 100,
                    coins: 0,
                    kills: 0
                });
                this._emit('player_joined', msg.playerId);
                break;
            }

            case 'state': {
                this._upsertRemotePlayer(msg.playerId, msg);
                break;
            }

            case 'player_left': {
                this._remotePlayers.delete(msg.playerId);
                this._emit('player_left', msg.playerId);
                break;
            }

            default:
                break;
        }
    }

    _upsertRemotePlayer(playerId, data) {
        const existing = this._remotePlayers.get(playerId);
        if (existing) {
            // Update target for interpolation; keep current x/y for lerp
            existing.targetX = data.x !== undefined ? data.x : existing.targetX;
            existing.targetY = data.y !== undefined ? data.y : existing.targetY;
            if (data.hp !== undefined) existing.hp = data.hp;
            if (data.coins !== undefined) existing.coins = data.coins;
            if (data.kills !== undefined) existing.kills = data.kills;
            if (data.name !== undefined) existing.name = data.name;
            if (data.skin !== undefined) existing.skin = data.skin;
        } else {
            this._remotePlayers.set(playerId, {
                playerId,
                name: data.name || 'Player',
                skin: data.skin || {},
                x: data.x || 0,
                y: data.y || 0,
                targetX: data.x || 0,
                targetY: data.y || 0,
                hp: data.hp !== undefined ? data.hp : 100,
                coins: data.coins || 0,
                kills: data.kills || 0
            });
        }
    }

    /** Render a single remote player in world-space. */
    _drawRemotePlayer(ctx, rp) {
        const size = 20;
        const x = rp.x;
        const y = rp.y;
        const skin = rp.skin || {};
        const color = skin.color || '#ff6644';
        const shape = skin.shape || 'cube';

        ctx.save();
        ctx.globalAlpha = 0.85;
        ctx.shadowBlur = 12;
        ctx.shadowColor = color;
        ctx.fillStyle = color;

        // Draw shape
        if (shape === 'triangle') {
            ctx.beginPath();
            ctx.moveTo(x, y - size);
            ctx.lineTo(x - size, y + size);
            ctx.lineTo(x + size, y + size);
            ctx.closePath();
            ctx.fill();
        } else if (shape === 'circle') {
            ctx.beginPath();
            ctx.arc(x, y, size, 0, Math.PI * 2);
            ctx.fill();
        } else if (shape === 'star') {
            this._drawStar(ctx, x, y, 5, size, size / 2);
            ctx.fill();
        } else {
            // Default: cube
            ctx.fillRect(x - size / 2, y - size / 2, size, size);
        }

        // Remote player indicator dot above
        ctx.shadowBlur = 0;
        ctx.globalAlpha = 1;
        ctx.fillStyle = '#aaffaa';
        ctx.beginPath();
        ctx.arc(x, y - size - 18, 4, 0, Math.PI * 2);
        ctx.fill();

        // Player name
        ctx.shadowBlur = 0;
        ctx.fillStyle = '#ffffff';
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 3;
        ctx.font = 'bold 13px Rajdhani, sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        const label = rp.name || 'Player';
        ctx.strokeText(label, x, y - size - 20);
        ctx.fillText(label, x, y - size - 20);

        // HP bar
        const barW = 36;
        const barH = 4;
        const barX = x - barW / 2;
        const barY = y + size + 6;
        const hpRatio = Math.max(0, Math.min(1, (rp.hp || 0) / 100));

        ctx.globalAlpha = 0.8;
        ctx.fillStyle = '#333';
        ctx.fillRect(barX, barY, barW, barH);
        ctx.fillStyle = hpRatio > 0.5 ? '#00ff88' : hpRatio > 0.25 ? '#ffaa00' : '#ff3333';
        ctx.fillRect(barX, barY, barW * hpRatio, barH);

        ctx.restore();
    }

    _drawStar(ctx, cx, cy, spikes, outerRadius, innerRadius) {
        let rot = Math.PI / 2 * 3;
        const step = Math.PI / spikes;

        ctx.beginPath();
        ctx.moveTo(cx, cy - outerRadius);
        for (let i = 0; i < spikes; i++) {
            ctx.lineTo(
                cx + Math.cos(rot) * outerRadius,
                cy + Math.sin(rot) * outerRadius
            );
            rot += step;
            ctx.lineTo(
                cx + Math.cos(rot) * innerRadius,
                cy + Math.sin(rot) * innerRadius
            );
            rot += step;
        }
        ctx.lineTo(cx, cy - outerRadius);
        ctx.closePath();
    }
}
